import { CommonModule, registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/id';
import {
  Component,
  computed,
  OnInit,
  Optional,
  signal,
  ViewChild,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbComponent } from '@merchant-portal/app/components/breadcrumb/breadcrumb.component';
import { BlockLoadingComponent } from '@merchant-portal/app/components/loading/block-loading/block-loading.component';
import { MobileDetailHeaderComponent } from '@merchant-portal/app/components/mobile-detail-header/mobile-detail-header.component';
import {
  DisbursementHistoryItem,
  StatusHistoryItem,
} from '@merchant-portal/app/models/disbursement/disbursement-history/disbursement-history.interface';
import { environment } from '@merchant-portal/environments/environment';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { StepperModule } from 'primeng/stepper';
import { ToastModule } from 'primeng/toast';
import { combineLatest, Observable, of } from 'rxjs';
import { BadgeComponent, BadgeType } from 'ui-component/src/lib/badge/badge.component';
import {
  DisbursementHistoryDetailFactoryService,
  DisbursementHistoryDetailState,
} from './factory/disbursement-history-detail-store-factory';
import { TransactionReasonTypePipe } from './pipe/transaction-type-reason.pipe';
import { DisbursementHistoryDetailStore } from './store/disbursement-history-detail.store';
registerLocaleData(localeFr, 'id');
@Component({
  selector: 'app-disbursement-history-detail',
  templateUrl: './disbursement-history-detail.component.html',
  styleUrls: ['./disbursement-history-detail.component.scss'],
  providers: [
    {
      provide: DisbursementHistoryDetailFactoryService,
      useClass: DisbursementHistoryDetailStore,
    },
  ],
  imports: [
    CommonModule,
    CardModule,
    InputTextModule,
    ReactiveFormsModule,
    ButtonModule,
    BlockLoadingComponent,
    ToastModule,
    TransactionReasonTypePipe,
    BreadcrumbComponent,
    BadgeComponent,
    StepperModule,
    MobileDetailHeaderComponent,
  ],
})
export class DisbursementHistoryDetailComponent implements OnInit {
  id = '';
  subMerchantId = '';
  vm$: Observable<DisbursementHistoryDetailState>;
  showTooltip = false;

  statusHistory: StatusHistoryItem[] = [];

  activeStep: number = 1;

  showAllStatuses = signal(false);

  @ViewChild(BreadcrumbComponent) breadcrumbComponent?: BreadcrumbComponent;

  constructor(
    private store: DisbursementHistoryDetailFactoryService,
    private route: ActivatedRoute,
    private router: Router,
    @Optional() private dialogRef?: DynamicDialogRef
  ) {
    this.vm$ = this.store.vm$;
  }

  ngOnInit() {
    combineLatest(this.route.paramMap, this.route.parent?.paramMap || of(null)).subscribe(
      ([params, parentParams]) => {
        this.id = params.get('id') || '';
        this.subMerchantId = parentParams?.get('subMerchantId') || '';
        this.store.requestDisbursementHistory({ id: this.id, subMerchantId: this.subMerchantId });
      }
    );

    this.vm$.subscribe(state => {
      if (state.downloadUrl) {
        window.open(state.downloadUrl, '_blank');
      }

      // Update status history when data changes
      if (state.currentDisbursementHistory) {
        this.updateStatusHistory(state.currentDisbursementHistory);
      }
    });
  }

  download(): void {
    this.store.downloadRecepit({ id: this.id, subMerchantId: this.subMerchantId });
  }

  private updateStatusHistory(data: DisbursementHistoryItem): void {
    // Use status history from backend if available, otherwise create empty array
    if (data.statusHistory && data.statusHistory.length > 0) {
      this.statusHistory = data.statusHistory.map(item => ({
        ...item,
        // Map timestamp to date for compatibility with existing template
        date: item.timestamp || item.date,
      }));
    } else {
      // Handle case when backend doesn't provide statusHistory yet
      this.statusHistory = [];
    }

    this.activeStep = this.statusHistory.filter(item => item.timestamp || item.date).length;
  }

  getBadgeType(status: string | undefined): BadgeType {
    const normalized = (status ?? '').toLowerCase();

    switch (normalized) {
      case 'approved':
      case 'success':
      case 'completed':
        return 'Success';
      case 'rejected':
      case 'failed':
        return 'Danger';
      case 'waiting':
      case 'pending':
        return 'Warning';
      case 'cancelled':
        return 'Neutral';
      default:
        return 'None';
    }
  }

  getTransactionStatusLabel(payload: DisbursementHistoryItem | null): string {
    if (payload == null) {
      return '-';
    }

    if (payload.reasonType === 'INSUFICIENT_BALANCE') {
      return '-';
    }

    if (payload.reasonType === 'CANCELLED') {
      return payload.reasonType;
    }

    return payload.transactionStatus ?? '-';
  }

  getOverallStatusLabel(data: DisbursementHistoryItem | null | undefined): string {
    if (!data) return '';

    const status = data.transactionStatus || '';
    switch (status.toLowerCase()) {
      case 'success':
        return 'Success';
      case 'failed':
        return 'Failed';
      case 'processing':
        return 'Processing';
      case 'pending':
        return 'Pending';
      default:
        return '';
    }
  }

  getOverallStatusBadgeType(data: DisbursementHistoryItem | null | undefined): BadgeType {
    if (!data) return 'None';

    const status = data.transactionStatus || '';
    switch (status.toLowerCase()) {
      case 'success':
        return 'Success';
      case 'failed':
        return 'Danger';
      case 'processing':
        return 'Warning';
      case 'pending':
        return 'Warning';
      default:
        return 'None';
    }
  }

  getStatusIconClass(step: any): string {
    const label = step.label?.toLowerCase() || '';
    const status = step.status?.toLowerCase() || '';

    if (!step.timestamp) {
      return 'grey-circle';
    }

    const hasFailedOutcome = this.statusHistory.some(
      s =>
        s.label?.toLowerCase().includes('failed') ||
        s.label?.toLowerCase().includes('cancelled') ||
        s.label?.toLowerCase().includes('rejected') ||
        s.status?.toLowerCase().includes('failed') ||
        s.status?.toLowerCase().includes('cancelled') ||
        s.status?.toLowerCase().includes('rejected')
    );

    const hasSuccessOutcome = this.statusHistory.some(
      s =>
        (s.label?.toLowerCase().includes('success') ||
          s.status?.toLowerCase().includes('success')) &&
        s.timestamp
    );

    const failedStates = [
      'failed',
      'cancelled',
      'rejected',
      'declined',
      'expired',
      'void',
      'error',
      'timeout',
      'blocked',
    ];

    if (failedStates.some(state => label.includes(state) || status.includes(state))) {
      return 'cross-circle';
    }

    const successStates = [
      'success',
      'completed',
      'approved',
      'done',
      'finished',
      'transferred',
      'settled',
      'paid',
    ];

    if (successStates.some(state => label.includes(state) || status.includes(state))) {
      return 'check-circle';
    }

    if (label.includes('created') || label.includes('payout created')) {
      if (hasSuccessOutcome && !hasFailedOutcome) {
        return 'check-circle';
      } else {
        return 'blue-circle';
      }
    }

    if (label.includes('processing') && (hasSuccessOutcome || hasFailedOutcome)) {
      return 'check-circle';
    }

    return 'blue-circle';
  }

  cancel(): void {
    this.dialogRef?.close();
  }

  shouldShowTopUpCTA(recommendation: string): boolean {
    if (!recommendation) return false;
    return recommendation.toLowerCase().includes('top up your balance');
  }

  shouldShowContactHelpdesk(recommendation: string): boolean {
    if (!recommendation) return false;
    return (
      recommendation.toLowerCase().includes('contact our customer support') ||
      recommendation.toLowerCase().includes('do not retry')
    );
  }

  showTopUpDialog() {
    if (environment.topUpSimulation) {
      this.router.navigateByUrl('/disbursement/top-up-simulation');
    } else {
      this.router.navigateByUrl('/disbursement/top-up-instruction');
    }
  }

  contactHelpdesk() {
    window.open('mailto:helpdesk@pivot-payment.com');
  }

  goBack(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
      return;
    }

    const { subMerchantId } = this;
    if (subMerchantId) {
      this.router.navigate([`/platform/detail/${subMerchantId}`]);
    } else {
      this.router.navigate(['/payout/payout-history']);
    }
  }

  toggleStatusView(): void {
    this.showAllStatuses.update(value => !value);
  }

  visibleStatusHistory = computed(() => {
    if (this.showAllStatuses()) {
      return this.statusHistory;
    } else {
      const activeStatuses = this.statusHistory.filter(item => item.timestamp || item.date);
      return activeStatuses.slice(-1);
    }
  });
}
